import { Center, Flex, Text, Title } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import { Button } from '../Elements';
import { ReactComponent as NoInternet } from '@/assets/svg/NoInternet.svg';
import { ASSETS_BUCKET_URL } from '@/config';

type IllustrationProps = {
  name: string;
  message?: string;
  clickAction?: () => void;
  mHeight?: string;
};
export const DefaultIllustration = ({
  name,
  message,
  clickAction,
  mHeight = 'auto',
}: IllustrationProps) => {
  const illustrationMap = [
    {
      name: 'CartExpired',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/CartExpired.svg`}
          alt="CartExpired"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'Your cart has expired',
      subHeading: 'Browse available food items and add them to your cart for placing an order.',
      redirectUrl: '/', // Redirecting to vendor list
      CTA: 'Browse food items',
    },
    {
      name: 'Connectivityissue',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/Connectivityissue.svg`}
          alt="Connectivityissue"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'Connectivity issue',
      subHeading: 'Please check your internet connection and try again.',
    },
    {
      name: 'EmptyCart',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/EmptyCart.svg`}
          alt="EmptyCart"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'Your cart is empty',
      subHeading: 'Browse available food items and add them to your cart for placing an order.',
      redirectUrl: '/', // Redirecting to vendor list
      CTA: 'Browse food items',
    },
    {
      name: 'EmptyFavourites',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/EmptyFavourites.svg`}
          alt="EmptyFavourites"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'Add Favourites',
      subHeading:
        "For quick checkouts, add any food item to your favourites by clicking on the 'Heart icon'.",
    },
    {
      name: 'ListingError',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/ListingError.svg`}
          alt="ListingError"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'Something went wrong',
      CTA: 'Browse Food Counters',
    },
    {
      name: 'NoAddressYet',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoAddressYet.svg`}
          alt="NoAddressYet"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: ' Add delivery address',
      subHeading: 'To get your food delivered to you, add delivery address.',
      // CTA: ' Add delivery address',
    },
    {
      name: 'NoInternet',
      component: <NoInternet />,
      heading: 'No internet connection',
      subHeading: 'Make sure wifi or cellular data is turned on and then try again',
      CTA: 'Try again',
    },
    {
      name: 'NoMenuItems',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoMenuItems.svg`}
          alt="NoMenuItems"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'No food items are available for this counter',
    },
    {
      name: 'NoOrdersYet',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoOrdersYet.svg`}
          alt="NoOrdersYet"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'No previous orders found',
    },
    {
      name: 'NoPaymentHistory',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoPaymentHistory.svg`}
          alt="NoPaymentHistory"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'No previous payments found',
    },
    {
      name: 'NoPaymentMethods',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoPaymentMethods.svg`}
          alt="NoPaymentMethods"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'No payment methods are available currently',
    },
    {
      name: 'NoSearchResults',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoSearchResults.svg`}
          alt="NoSearchResults"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'No  results found',
      subHeading: 'Try searching for another food item',
    },
    {
      name: 'NoVendorsAvailable',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoVendorsAvailable.svg`}
          alt="NoVendorsAvailable"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'No food counters are open for this cafeteria',
    },
    {
      name: 'NoOccasionsAvailable',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoVendorsAvailable.svg`}
          alt="NoOccasionsAvailable"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'Currently there are no Occasions Available',
      CTA: 'Try Again',
    },
    {
      name: 'SearchForAnything',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/SearchForAnything.svg`}
          alt="SearchForAnything"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
    },
    {
      name: 'SomethingWentWrong',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/SomethingWentWrong.svg`}
          alt="SomethingWentWrong"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'Something went wrong ',
      subHeading: 'Please get in touch with HungerBox help desk',
      CTA: 'Try again',
    },
    {
      name: 'Timeout',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/Timeout.svg`}
          alt="Timeout"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: ' Request timed out ',
      subHeading: 'The request is taking longer to process. This has been timed out by the server',
    },
    {
      name: 'NoOrderFound',
      component: (
        <img
          src={`${ASSETS_BUCKET_URL}/svg/NoOrdersYet.svg`}
          alt="NoOrdersYet"
          loading="lazy"
          className="default-illustation-img"
        />
      ),
      heading: 'No order found',
    },
  ];

  const illustration = illustrationMap.find((item) => item.name === name);
  const isMobile = useMediaQuery('(max-width: 768px)', true);

  const navigate = useNavigate();

  return (
    <Center
      style={{
        padding: '0 1rem',
        height: isMobile ? mHeight : '70vh',
        width: '100%',
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {illustration && (
        <Flex direction="column" align="center" justify="center" h="100%" w="100%">
          {illustration.component}

          <Title
            order={3}
            mt="lg"
            fw="bold"
            style={{
              maxWidth: '600px',
            }}
            align="center"
          >
            {illustration.heading}
          </Title>
          <Text
            mt="sm"
            fz={{
              base: 'md',
              md: 'lg',
            }}
            color="gray"
            lh={1.2}
            style={{
              maxWidth: '700px',
              padding: '0 2rem',
            }}
            w={isMobile ? '100%' : '60%'}
            align="center"
          >
            {message ?? illustration.subHeading}
          </Text>
          {illustration.CTA && (
            <Button
              mt="lg"
              onClick={() => {
                if (illustration.redirectUrl) {
                  navigate(illustration.redirectUrl);
                  return;
                }
                if (clickAction) {
                  clickAction();
                  return;
                }
                window.location.reload();
              }}
              fullWidth
              w={isMobile ? '100%' : '50%'}
            >
              {illustration.CTA}
            </Button>
          )}
        </Flex>
      )}
    </Center>
  );
};
